import React, { useMemo } from 'react';
import { useProducts } from '../../hooks/useOfflineData.js';

interface Product {
  id: string;
  name: string;
  manufacturer_id: string;
  manufacturer_name: string;
  campaign_name?: string;
}

interface ProductSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  hideLabel?: boolean;
  className?: string;
}

export const ProductSelector: React.FC<ProductSelectorProps> = ({
  value,
  onChange,
  error,
  hideLabel = false,
  className,
}) => {
  const { products, loading } = useProducts();

  const grouped = useMemo(() => {
    const map = new Map<string, { manufacturerName: string; products: Product[] }>();
    for (const p of products as Product[]) {
      if (!map.has(p.manufacturer_id)) {
        map.set(p.manufacturer_id, { manufacturerName: p.manufacturer_name, products: [] });
      }
      map.get(p.manufacturer_id)!.products.push(p);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.manufacturerName.localeCompare(b.manufacturerName),
    );
  }, [products]);

  if (loading) return <div className="text-sm text-gray-500">Loading products…</div>;

  const selectEl = (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className={
        className ||
        `mt-1 block w-full rounded-md border-gray-300 shadow-sm border p-2 focus:ring-brand-500 focus:border-brand-500 ${
          error ? 'border-red-500' : ''
        }`
      }
    >
      <option value="">-- Choose Product --</option>
      {grouped.map(({ manufacturerName, products: group }) => (
        <optgroup key={manufacturerName} label={manufacturerName}>
          {group.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.campaign_name ? ` (${p.campaign_name})` : ''}
            </option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  if (hideLabel) {
    return (
      <>
        {selectEl}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
      </>
    );
  }

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">Select Product/Campaign</label>
      {selectEl}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};
